export class AudioService {
  constructor() {
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    this.audioElement = null;
    this.audioSource = null;
    this.isPlaying = false;
    this.preloadedBuffers = new Map();
    this.preloadPromise = null;
    this.listeners = {
      playing: new Set(),
      stopped: new Set()
    };
  }

  checkBrowserSupport(sourceUrl) {
    const ext = sourceUrl.split('.').pop()?.toLowerCase();
    const audio = new Audio();

    // 检查 AMR 支持
    if (ext === 'amr') {
      return 'amr';
    }

    // 检查 OGG 支持
    if (ext === 'ogg' &&
        (/iPhone|iPad|iPod/i.test(navigator.userAgent) || !audio.canPlayType('audio/ogg'))) {
      return 'ogg';
    }

    return 'native';
  }

  async handleAMR(sourceUrl) {
    const response = await fetch(sourceUrl);
    const arrayBuffer = await response.arrayBuffer();
    const model = await import(
      /* webpackChunkName: "amr-decoder" */
      /* webpackMode: "lazy" */
      'benz-amr-recorder'
    );
    const BenzAMRRecorder = model.default || model;
    const amr = new BenzAMRRecorder();
    const pcmBuffer = await amr.decodeAMRAsync(new Uint8Array(arrayBuffer));

    // 创建 AudioBuffer
    // 注意：您需要知道音频的采样率和声道数
    const sampleRate = 8000; // AMR 通常是 8kHz，但请确认您的具体情况
    const numberOfChannels = 1; // AMR 通常是单声道，但也请确认

    const audioBuffer = this.audioContext.createBuffer(numberOfChannels, pcmBuffer.length, sampleRate);

    // 将 PCM 数据复制到 AudioBuffer
    // 如果是单声道，我们只需要使用第一个声道
    audioBuffer.copyToChannel(pcmBuffer, 0);

    return audioBuffer;
  }

  async handleOGG(sourceUrl) {
    const response = await fetch(sourceUrl);
    const arrayBuffer = await response.arrayBuffer();
    const model = await import(
      /* webpackChunkName: "ogg-decoder" */
      /* webpackMode: "lazy" */
      'audio-decode'
    );
    const OggDecoder = model.default || model;
    return await OggDecoder(arrayBuffer);
  }

  async handleNative(sourceUrl) {
    this.audioElement = new Audio(sourceUrl);
    await this.audioElement.play();
  }

  async preload(sourceUrl) {
    try {
      const supportType = this.checkBrowserSupport(sourceUrl);
      let buffer;

      switch (supportType) {
        case 'amr':
          buffer = await this.handleAMR(sourceUrl);
          break;

        case 'ogg':
          buffer = await this.handleOGG(sourceUrl);
          break;

        case 'native':
          // 对于原生支持的格式，创建一个Audio元素并预加载
          const audio = new Audio();
          audio.preload = 'auto';
          audio.src = sourceUrl;
          await new Promise((resolve) => {
            audio.addEventListener('canplaythrough', resolve, { once: true });
            audio.load();
          });
          buffer = audio;
          break;
      }

      this.preloadedBuffers.set(sourceUrl, {
        type: supportType,
        buffer: buffer
      });

      return buffer;
    } catch (error) {
      console.error('Error preloading audio:', error);
      throw error;
    }
  }

  async play(sourceUrl) {
    try {
      // 停止当前正在播放的音频
      await this.stop();

      // 检查是否有预加载的buffer
      const preloadedData = this.preloadedBuffers.get(sourceUrl);

      if (preloadedData) {
        // 使用预加载的buffer
        if (preloadedData.type === 'native') {
          this.audioElement = preloadedData.buffer;
          this.audioElement.addEventListener('ended', () => this.stop(), { once: true });
          await this.audioElement.play();
        } else {
          this.audioSource = this.audioContext.createBufferSource();
          this.audioSource.buffer = preloadedData.buffer;
          this.audioSource.addEventListener('ended', () => this.stop(), { once: true });
          this.audioSource.connect(this.audioContext.destination);
          this.audioSource.start();
        }
      } else {
        // 如果没有预加载，按原来的方式加载和播放
        const supportType = this.checkBrowserSupport(sourceUrl);

        switch (supportType) {
          case 'amr':
            const amrBuffer = await this.handleAMR(sourceUrl);
            this.audioSource = this.audioContext.createBufferSource();
            this.audioSource.buffer = amrBuffer;
            this.audioSource.connect(this.audioContext.destination);
            this.audioSource.start();
            break;

          case 'ogg':
            const oggBuffer = await this.handleOGG(sourceUrl);
            this.audioSource = this.audioContext.createBufferSource();
            this.audioSource.buffer = oggBuffer;
            this.audioSource.addEventListener('ended', () => this.stop(), { once: true });
            this.audioSource.connect(this.audioContext.destination);
            this.audioSource.start();
            break;

          case 'native':
            await this.handleNative(sourceUrl);
            this.audioElement.addEventListener('ended', () => this.stop(), { once: true });
            break;
        }
      }

      this.isPlaying = true;
      this.emit('playing');
    } catch (error) {
      console.error('Error playing audio:', error);
      throw error;
    }
  }

  async stop() {
    if (this.audioSource) {
      try {
        this.audioSource.stop();
        this.audioSource.disconnect();
        this.audioSource = null;
      } catch (error) {
        console.error('Error stopping audio source:', error);
      }
    }

    if (this.audioElement) {
      try {
        this.audioElement.pause();
        this.audioElement.currentTime = 0;
        this.audioElement = null;
      } catch (error) {
        console.error('Error stopping audio element:', error);
      }
    }

    this.isPlaying = false;
    this.emit('stopped');
  }

  async init(sourceUrl) {
    // 确保只执行一次预加载
    if (!this.preloadPromise) {
      this.preloadPromise = this.preload(sourceUrl);
    }

    // 等待预加载完成
    const buffer = await this.preloadPromise;

    // 返回播放控制方法
    return {
      duration: (buffer instanceof Audio) ? buffer.duration : buffer.duration,
    };
  }

  addEventListener(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].add(callback);
    }
  }

  removeEventListener(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].delete(callback);
    }
  }

  emit(event) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback());
    }
  }
}
